import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronsLeft, ChevronsRight } from "lucide-react";
import { canAccessPage, NAV_ITEMS } from "@/app/router/nav.config";
import { useMyPageAccess } from "@/features/team/useTeam";
import { useAuthStore } from "@/store/auth.store";
import { cn } from "@/utils/cn";

export function Sidebar({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const isSuperAdmin = useAuthStore((s) => s.user?.role === "SUPER_ADMIN");
  const { data } = useMyPageAccess();
  const location = useLocation();
  const [expanded, setExpanded] = useState<string | null>(() =>
    NAV_ITEMS.find((n) => n.children && location.pathname.startsWith(n.path))?.path ?? null,
  );

  const visibleItems = NAV_ITEMS.filter((item) => canAccessPage(item.path, isSuperAdmin, data?.pages));

  return (
    <aside
      className={cn(
        "flex h-full flex-col border-r border-border bg-cream-50 transition-[width]",
        collapsed ? "w-[68px]" : "w-60",
      )}
    >
      <div className="flex h-14 items-center gap-2 border-b border-border px-4">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-teal-500 text-sm font-bold text-cream-50">
          E
        </div>
        {!collapsed && (
          <span className="truncate text-sm font-semibold text-ink">
            Eyy <span className="text-gold-600">Admin</span>
          </span>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <ul className="flex flex-col gap-1">
          {visibleItems.map((item) =>
            item.children ? (
              <li key={item.path}>
                <button
                  type="button"
                  onClick={() => setExpanded((e) => (e === item.path ? null : item.path))}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                    location.pathname.startsWith(item.path)
                      ? "text-ink"
                      : "text-ink-muted hover:bg-cream-200 hover:text-ink",
                  )}
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="flex-1 truncate text-left">{item.label}</span>
                      <ChevronDown
                        className={cn(
                          "h-3.5 w-3.5 shrink-0 transition-transform",
                          expanded === item.path && "rotate-180",
                        )}
                      />
                    </>
                  )}
                </button>
                {!collapsed && expanded === item.path && (
                  <ul className="mt-1 flex flex-col gap-1 pl-7">
                    {item.children.map((child) => (
                      <li key={child.path}>
                        <NavLink
                          to={child.path}
                          className={({ isActive }) =>
                            cn(
                              "block truncate rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                              isActive
                                ? "bg-teal-500 text-cream-50"
                                : "text-ink-muted hover:bg-cream-200 hover:text-ink",
                            )
                          }
                        >
                          {child.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ) : (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-teal-500 text-cream-50"
                        : "text-ink-muted hover:bg-cream-200 hover:text-ink",
                    )
                  }
                  title={collapsed ? item.label : undefined}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              </li>
            ),
          )}
        </ul>
      </nav>

      <div className="flex items-center justify-between border-t border-border px-3 py-3">
        {!collapsed && <span className="text-xs text-ink-faint">v1 · ops</span>}
        <button
          onClick={onToggle}
          className="rounded-md p-1.5 text-ink-faint hover:bg-cream-200"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
        </button>
      </div>
    </aside>
  );
}
